import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  alpha,
  Chip,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ConstructionIcon from '@mui/icons-material/Construction';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useChains } from '../../contexts/ChainContext';
import { chainCache } from '../../contexts/ChainContext';

// Helper function to get chain logo from cache or context
const getChainLogo = (chainValue) => {
  if (!chainValue) return null;
  
  // Try global cache first (faster and available before context is ready)
  if (chainCache.logoMap.has(chainValue.toLowerCase())) {
    return chainCache.logoMap.get(chainValue.toLowerCase());
  }
  
  // Try the chains array in the global cache
  if (chainCache.chains.length > 0) {
    const cachedChain = chainCache.chains.find(c => 
      c.name && c.name.toLowerCase() === chainValue.toLowerCase()
    );
    if (cachedChain?.logoUrl) {
      // Update the map for future lookups
      chainCache.logoMap.set(chainValue.toLowerCase(), cachedChain.logoUrl);
      return cachedChain.logoUrl;
    }
  }
  
  // Will be updated when context loads
  return null;
};

// Helper function to format opening date
const formatOpeningDate = (openingDate) => {
  if (!openingDate) return null;
  
  try {
    const date = new Date(openingDate);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Format the actual date as dd/mm/yy
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const formattedDate = `${day}/${month}/${year}`;
    
    if (diffDays < 0) {
      // Past date
      const daysAgo = Math.abs(diffDays);
      let relativeText;
      if (daysAgo === 1) relativeText = 'Opened yesterday';
      else if (daysAgo < 7) relativeText = `Opened ${daysAgo} days ago`;
      else if (daysAgo < 30) relativeText = `Opened ${Math.floor(daysAgo / 7)} week${Math.floor(daysAgo / 7) > 1 ? 's' : ''} ago`;
      else relativeText = `Opened ${Math.floor(daysAgo / 30)} month${Math.floor(daysAgo / 30) > 1 ? 's' : ''} ago`;
      
      return `${relativeText} (${formattedDate})`;
    } else if (diffDays === 0) {
      return `Opening today (${formattedDate})`;
    } else {
      // Future date
      let relativeText;
      if (diffDays === 1) relativeText = 'Opening tomorrow';
      else if (diffDays < 7) relativeText = `Opening in ${diffDays} days`;
      else if (diffDays < 30) relativeText = `Opening in ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
      else if (diffDays < 365) relativeText = `Opening in ${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''}`;
      else relativeText = `Opening in ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''}`;
      
      return `${relativeText} (${formattedDate})`;
    }
  } catch (error) {
    console.warn('Invalid opening date format:', openingDate);
    return null;
  }
};

const HotelCard = React.memo(({ hotel, selected, onClick }) => {
  // Get chains directly from context
  const { getChainLogoUrl } = useChains();
  
  // Memoize getCategoryValue function to maintain stable reference
  const getCategoryValue = useCallback((fieldName) => {
    if (!hotel?.categoryData) return null;
    return hotel.categoryData[`category_${fieldName.toLowerCase()}`]?.value || null;
  }, [hotel?.categoryData]);

  // Memoize chain value using memoized getCategoryValue
  const chainValue = useMemo(() => getCategoryValue('chain'), [getCategoryValue]);
  
  // Get logo URL first from cache, then from context
  const chainLogoUrl = useMemo(() => {
    if (!chainValue) return null;
    
    // First try the global cache (faster, works before context is ready)
    const cachedLogo = getChainLogo(chainValue);
    if (cachedLogo) return cachedLogo;
    
    // Fall back to context method
    return getChainLogoUrl?.(chainValue);
  }, [chainValue, getChainLogoUrl]);

  return (
    <Box
      component="li"
      onClick={onClick}
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        borderRadius: 3,
        transition: 'all 0.2s ease',
        bgcolor: selected 
          ? theme => alpha(theme.palette.primary.main, 0.08)
          : 'background.paper',
        '&:hover': {
          bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
        }
      }}
    >
      {/* Brand Image */}
      <Box sx={{ 
        width: 60,
        height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        flexShrink: 0,
      }}>
            {chainLogoUrl ? (
              <Box
                component="img"
                src={chainLogoUrl}
                alt={chainValue}
                loading="lazy"
                sx={{
              height: '100%',
                  width: 'auto',
              maxWidth: '100%',
                  objectFit: 'contain',
                  filter: theme => theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none',
                }}
              />
            ) : chainValue ? (
              // Show nothing while logo is loading but we know the chain exists
              <Box 
                sx={{ 
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            ) : (
              <BusinessIcon sx={{ 
                color: 'text.disabled', 
            fontSize: 32,
                opacity: 0.5
              }} />
            )}
          </Box>
          
              {/* Hotel Info */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0, // Allow text to shrink
        }}>
          {/* Hotel Name and WIP Status */}
            <Box sx={{ 
              display: 'flex', 
            alignItems: 'center', 
              gap: 1,
            mb: 0.5,
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'StrawberrySansDisplay',
                fontWeight: 500,
                  color: 'text.primary',
                fontSize: '1.3rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                }}
                title={getCategoryValue('name')}
              >
                {getCategoryValue('name')}
              </Typography>
            
            {/* WIP Indicator */}
              {hotel.status === 'work_in_progress' && (
                  <Chip
                    icon={<ConstructionIcon />}
                label="WIP"
                    size="small"
                    sx={{
                      height: 24,
                  bgcolor: theme => alpha(theme.palette.warning.main, 0.1),
                  color: 'warning.main',
                      border: '1px solid',
                  borderColor: theme => alpha(theme.palette.warning.main, 0.2),
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      },
                      '& .MuiChip-icon': {
                        fontSize: '0.875rem',
                        ml: 0.5
                      }
                    }}
                  />
            )}
            
            {/* Archived Indicator */}
              {hotel.status === 'archived' && (
                  <Chip
                    icon={<ArchiveIcon />}
                label="ARCHIVED"
                    size="small"
                    sx={{
                      height: 24,
                  bgcolor: theme => alpha(theme.palette.error.main, 0.1),
                  color: 'error.main',
                      border: '1px solid',
                  borderColor: theme => alpha(theme.palette.error.main, 0.2),
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      },
                      '& .MuiChip-icon': {
                        fontSize: '0.875rem',
                        ml: 0.5
                      }
                    }}
                  />
            )}
          </Box>

          {/* ID | Ownership */}
          <Typography 
            variant="body2" 
                sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
                    lineHeight: 1.2,
            }}
          >
            {getCategoryValue('propertynumber') && `${getCategoryValue('propertynumber')} | `}
            {getCategoryValue('ownership') || 'No ownership info'}
          </Typography>

          {/* Opening Date - Only show for WIP hotels */}
          {hotel.status === 'work_in_progress' && hotel.expectedOpeningDate && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              mt: 0.5
            }}>
              <CalendarTodayIcon sx={{ 
                fontSize: '0.875rem', 
                color: 'text.secondary',
                opacity: 0.7
              }} />
              <Typography 
                variant="body2" 
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  lineHeight: 1.2,
                  fontStyle: 'italic'
                }}
              >
                {formatOpeningDate(hotel.expectedOpeningDate)}
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  );
});

HotelCard.displayName = 'HotelCard';

export default HotelCard; 